<template>
  <div class="library__article">
    <p>
      <VisibleText>Go to </VisibleText>
      <router-link to="/library">
        <VisibleText>Library</VisibleText>
      </router-link>
    </p>

    <h1 class="library__h1">
      <VisibleText>What Social Media Tells You That Surveys Never Will</VisibleText>
    </h1>

    <div class="library__heading-line"></div>

    <p>
      <VisibleText>2026-06-02</VisibleText>
    </p>

    <img
      class="library__image"
      style="max-height: 15rem; max-width: 95%"
      src="/assets/landing/landing_DMV.jpg"
      alt=""
    />

    <h2 class="library__h2">
      <VisibleText>
        How applying Failure Demand analysis to 1,200+ public reviews revealed the systemic failures
        behind a state motor vehicle agency's worst-performing locations
      </VisibleText>
    </h2>

    <p>
      <VisibleText>
        Every major government service agency in North America runs a satisfaction survey. Most
        track wait times, staff courtesy, and whether the client got what they came for. Most
        produce quarterly reports showing scores that move within a narrow band, year over year,
        without ever identifying why the agency's phones ring as much as they do, or why the same
        clients keep coming back for the same transactions.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        The reason those surveys cannot answer those questions is structural. They are designed to
        measure what happens when a transaction succeeds. They are largely blind to the system
        conditions that generate the transaction in the first place - and to the cumulative cost of
        the transactions that should never have occurred.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        When ResearchAI applied a Failure Demand analytical framework to publicly available Google
        Reviews from a US state motor vehicle agency - over 1,200 reviews across multiple
        high-volume service locations - what emerged was a picture of service system performance
        that the agency's own measurement programme had never produced. Not because the evidence was
        absent. Because no one had asked the right questions of the right data.
      </VisibleText>
    </p>

    <h2 class="library__h2">
      <VisibleText>The Analytical Problem: Why Surveys Miss Systemic Failure</VisibleText>
    </h2>

    <p>
      <VisibleText>
        Failure Demand is a concept developed by John Seddon in the Vanguard Method. It
        distinguishes between value demand - legitimate first contact: a citizen who needs to renew
        a licence - and failure demand: demand created by the organisation's own service failures. A
        citizen who calls because their online renewal failed. A citizen who returns because they
        were sent away for a document they weren't told to bring. A citizen who makes a third
        appointment because the first two produced contradictory instructions.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        The defining characteristic of failure demand is that it is entirely preventable. It is not
        the consequence of high service take-up. It is the consequence of a service system
        generating its own workload through failures it could eliminate.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        Satisfaction surveys cannot see failure demand for a structural reason: by the time a
        citizen fills in a survey, they have already resolved their transaction. A client who made
        three visits, was given wrong information twice, waited four hours on their final visit, and
        ultimately got their licence renewed - registers in the CSAT data as a satisfied customer.
        The two failed contacts, the six hours of cumulative wasted time, and the staff capacity
        absorbed by those contacts do not appear anywhere in the performance record.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        Social media reviews do not have this limitation. Citizens write them at the moment of
        failure, often in detail, and without the softening effect of a structured survey
        instrument. The language they use - 'came back,' 'sent me away,' 'had to return,' 'different
        answer every time,' 'third time here for the same thing' - are the linguistic fingerprints
        of failure demand. A validated dictionary can identify them at scale.
      </VisibleText>
    </p>

    <h2 class="library__h2">
      <VisibleText>Six Failure Demand Categories: What the Reviews Revealed</VisibleText>
    </h2>

    <p>
      <VisibleText>
        A 206-term Failure Demand dictionary was built from the ground up using the DMV review
        dataset - keywords, failure indicators, and phrase patterns drawn from actual citizen
        language, not from a generic template. The dictionary identified six categories of failure
        demand, each generating a distinct type of preventable contact.
      </VisibleText>
    </p>

    <table>
      <thead>
        <tr>
          <th>FD</th>
          <th>Category</th>
          <th>Signal Language</th>
          <th>What It Reveals</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>1</td>
          <td>Repeat Visits</td>
          <td>'back again,' 'third time,' 'had to return,' 'came back'</td>
          <td>
            The single highest-severity category. Each repeat visit represents a service failure
            that should have been resolved on first contact and was not.
          </td>
        </tr>
        <tr>
          <td>2</td>
          <td>Appointment System Failures</td>
          <td>'appointment cancelled,' 'no record of my booking,' 'showed up and no one knew'</td>
          <td>
            The booking system generating demand for its own failures - citizens contacting the
            agency to resolve appointment system problems rather than to transact.
          </td>
        </tr>
        <tr>
          <td>3</td>
          <td>Documentation &amp; Requirements</td>
          <td>
            'didn't know I needed,' ‘nobody told me,' 'rejected my documents,' 'different answer'
          </td>
          <td>
            Inconsistent or inadequately communicated document requirements sending citizens away
            unprepared and generating mandatory repeat contacts.
          </td>
        </tr>
        <tr>
          <td>4</td>
          <td>Information Inconsistency</td>
          <td>
            'told me different things,' 'conflicting information,' 'one person said yes, another
            said no'
          </td>
          <td>
            The most direct trust-damaging category: the agency is contradicting itself across
            staff, channels, and contacts for the same transaction.
          </td>
        </tr>
        <tr>
          <td>5</td>
          <td>System &amp; Technology Failures</td>
          <td>
            'website down,' 'system not working,' 'couldn't complete online,' 'had to come in'
          </td>
          <td>
            Digital channels that drive citizens back to in-person service, absorbing physical
            location capacity that should not have been needed.
          </td>
        </tr>
        <tr>
          <td>6</td>
          <td>Channel Switching</td>
          <td>
            'called first and then had to go in,' 'website said one thing, office said another'
          </td>
          <td>
            Multi-channel journeys that should have resolved on first contact but did not - each
            additional channel contact representing a system failure.
          </td>
        </tr>
      </tbody>
    </table>

    <p>
      <VisibleText>
        Across the 1,200+ reviews analyzed, 31.5% of negative reviews contained explicit failure
        demand signals - contacts that the service system generated through its own failures, not
        through legitimate new citizen need. This is not a satisfaction problem. It is a capacity
        and cost problem: a substantial fraction of the agency's service volume is preventable.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        "31.5% of negative reviews described contacts the service system generated through its own
        failures. These are not dissatisfied customers. They are symptoms of a system consuming its
        own capacity."
      </VisibleText>
    </p>

    <h3 class="library__h3">
      <VisibleText>The Trust Erosion Layer: From Frustration to Abandonment</VisibleText>
    </h3>

    <p>
      <VisibleText>
        Alongside the Failure Demand analysis, a Trust Erosion detection framework was applied to
        the same dataset - drawing on the Sludge Audit taxonomy developed by Sunstein and Thaler,
        which identifies cumulative friction and bureaucratic burden imposed on citizens even in
        transactions that ultimately succeed.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        Trust erosion is what happens to citizens who encounter failure demand repeatedly. It is not
        a satisfaction measurement. It is a trajectory - a progression from initial disappointment
        through to the point where a citizen stops engaging with the service entirely, not because
        their need has been met, but because they have given up on the system responding. The
        five-stage model captures this arc.
      </VisibleText>
    </p>

    <table>
      <thead>
        <tr>
          <th>Stage</th>
          <th>Label</th>
          <th>Citizen Signal</th>
          <th>% of Trust Erosion Cases</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>1</td>
          <td>Disappointment</td>
          <td>
            Single negative experience; still expects improvement. Language is specific and
            transactional.
          </td>
          <td>12.4%</td>
        </tr>
        <tr>
          <td>2</td>
          <td>System Frustration</td>
          <td>
            Pattern recognized: this is not a one-off. Language shifts from 'this happened' to 'this
            always happens.'
          </td>
          <td>42.6%</td>
        </tr>
        <tr>
          <td>3</td>
          <td>Competence Questioning</td>
          <td>
            Citizens begin questioning whether staff and systems are capable of resolving their
            issue at all.
          </td>
          <td>28.3%</td>
        </tr>
        <tr>
          <td>4</td>
          <td>Systemic Distrust</td>
          <td>
            Institutional distrust: not just this location, but the agency and the system behind it.
            Language becomes generalizing.
          </td>
          <td>12.8%</td>
        </tr>
        <tr>
          <td>5</td>
          <td>Active Avoidance</td>
          <td>
            Citizens describe choosing not to use the service or changing behaviour to minimize
            contact. The agency has lost them.
          </td>
          <td>3.9%</td>
        </tr>
      </tbody>
    </table>

    <p>
      <VisibleText>
        14.2% of all negative reviews showed trust erosion signals. The distribution matters: 42.6%
        of trust erosion cases were at Stage 2 - System Frustration - meaning the majority of
        citizens experiencing trust erosion had moved past isolated disappointment into pattern
        recognition. They have learned that the system behaves this way. Recovering their confidence
        requires not just a better interaction, but evidence that the system itself has changed.
      </VisibleText>
    </p>

    <h2 class="library__h2">
      <VisibleText>What This Means for Service Improvement</VisibleText>
    </h2>

    <p>
      <VisibleText>
        The most important finding from this analysis is not any individual failure category or
        trust erosion percentage. It is the relationship between the two. Failure demand and trust
        erosion are not independent problems - they are cause and consequence. Citizens who
        encounter repeat visit demand, documentation inconsistency, or appointment system failures
        at Stage 1 are the same citizens who appear at Stage 2 and Stage 3 if the conditions that
        generated the first failure are not corrected.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        This means that Failure Demand reduction is the most direct available lever for trust
        erosion prevention. The three highest-impact interventions identified in this analysis were
        precisely the categories that generated the most Stage 2 trust erosion cases: consistent
        pre-visit document requirements communicated across all channels, appointment system
        reliability with confirmation protocols, and elimination of information inconsistency
        between staff and digital channels for the same transaction.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        None of these interventions require capital investment. They require service system redesign
        - the kind of targeted, evidence-based redesign that becomes possible only when the
        organization can see the failure patterns that its routine measurement programme cannot
        produce. Social media data, analyzed with a validated Failure Demand framework, makes those
        patterns visible.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        "The survey told the agency how satisfied citizens were. The Failure Demand analysis told
        them why the agency's phones kept ringing. Only one of those questions leads to a systemic
        improvement."
      </VisibleText>
    </p>

    <table>
      <thead>
        <tr>
          Next in the series:
          <router-link to="/library/feature/trusted_ai_series_building_institutional_knowledge"
            >Article 5 — Building Institutional Knowledge.</router-link
          >
        </tr>
      </thead>
    </table>

    <h3 class="library__h3">
      <VisibleText>About ResearchAI</VisibleText>
    </h3>

    <p>
      <VisibleText>
        ResearchAI (research-ai.io) is a Canadian text analytics platform that combines traditional
        data science with strategically applied GenAI to transform unstructured feedback into
        validated organizational intelligence. The platform's Failure Demand and Sludge Audit
        analytical frameworks are grounded in the Vanguard Method (Seddon) and the sludge taxonomy
        developed by Sunstein and Thaler.
      </VisibleText>
    </p>

    <div class="library__textbox" style="padding: 0.5em">
      <div class="library__textbox__col">
        <img
          class="library__image library__image--nonresponsive"
          style="max-width: 4rem"
          src="/assets/landing/analyze_icon.png"
          alt=""
        />
      </div>
      <div class="library__textbox__col">
        <ul>
          <li>
            <VisibleText>
              If you are an existing user,
              <router-link to="/login">login to ResearchAI</router-link>
              and try something new. Or reach out to us at:
              <a href="mailto:hello@researchai.io">hello@researchai.io</a>
            </VisibleText>
          </li>
          <li>
            <VisibleText>
              If you are finding us for the first time,
              <router-link to="/register">register for a free ResearchAI account.</router-link>
              We will be in touch to set you up for success.
            </VisibleText>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { useHead } from '@unhead/vue';
import { computed, reactive } from 'vue';

export default {
  name: 'TrustedAISeries_DMV',
  setup() {
    const siteData = reactive({
      title: 'The Trusted AI Series · What Social Media Tells You That Surveys Never Will',
      description:
        'How applying Failure Demand analysis to 1,200+ public reviews revealed the systemic failures behind a state motor vehicle agency’s worst-performing locations.',
    });

    useHead({
      title: computed(() => siteData.title),
      meta: [
        {
          name: 'description',
          content: computed(() => siteData.description),
        },
      ],
    });

    return { siteData };
  },

  methods: {
    goto(refName) {
      this.$refs[refName].scrollIntoView();
    },
  },
};
</script>
